// Route (b): the painted block around the Tavern (studio cycle 003; streamed and compressed since cycle 006). THE DEFAULT for
// every player since studio 008c (Matt, 2026-10-23: "I'd like the game to always use the new, better graphics"); ?art=a is
// the old look, kept only as a fallback.
//
// The engine's own geometry stays: every wall column, roof and prop strip in the block keeps its sprite, its depth band, its
// cutaway and its stub; only its TEXTURE changes, to a frame of a painted TILE at the column's own screen rect. The ground
// had a painted PLATE over its chunks until studio 011 (#41: it drifted off the walkable map; the ground kit, groundkit.ts,
// draws the street from the cells now, and only ?plate=1 shows a plate). Each image is registered in world-screen logical px at texture resolution RES (3): a
// phone at scale 3 draws one painted texel per device pixel.
//
// Cycle 006 (spec 006 items 1, 2, 5): the block is cut by tools/art/paint_b.py tiles on the engine's chunk grid (the screen
// box of each chunk diamond with kx + ky even: 512 x 256 logical px, no overlap) as KTX2 (UASTC/ETC1S, transcoded by the
// self-hosted libktx in a worker to bc7 / astc / etc2, 1 byte a texel on the GPU). Every slice and roof mask that used to be
// cut on a 2D canvas here is baked offline. A wall column or prop strip takes the tile its FOOT stands in (paint_b gives
// every pixel to the tile of its own run's foot, with a margin, so that one texture holds the whole column).
//   - streaming: the tiles the view needs, plus a margin and a prefetch along the camera's motion, load as one unit per SET
//     (day, night, snow); an LRU under a hard byte cap unloads the rest (the texture and its GPU memory destroyed)
//   - sets: the painted variants share the geometry. Only the set in use is resident; at dusk and dawn both are, and the
//     night set fades in over the day set (an overlay sprite per painted column, the plate and roofs likewise), under the
//     existing light grade (the lightmap's ambient darkens the day set; inside the painted footprint the light RT reaches
//     1.0 by the night set's share, because the night set carries its own light)
//   - fail closed: a tile that fails to load is COUNTED (chunks failed, in the ?perf=1 readout) and its columns keep
//     today's art; a damaged wall column keeps its painting, darkened, and is counted too
//
// ?art is read ONCE, here, at module load: absent (or anything but a) is the painted block; ?art=a is the pre-008 game exactly
// (nothing below runs).
import { Assets, CanvasSource, CompressedSource, Container, Filter, GlProgram, Rectangle, Sprite, Texture, defaultFilterVert, getSupportedTextureFormats } from 'pixi.js';
import { ALPHA_PROXY } from './iso_render';

const Q = new URLSearchParams(location.search);
/** a painted texture swapped on a sprite: its render group rebuilds its instructions (studio 008c, the G3 freeze). Pixi
 *  keeps a band's batches until the band's structure changes, and a band off screen is not drawn, so a sprite moved off a
 *  tile's frame could leave that tile's source in a cached batch; when the LRU then destroyed the tile, the next render
 *  of that band bound a destroyed source ("reading 'addressModeU'") and the game loop stopped. */
const rebuild = (sp: Sprite) => { const g = (sp as any).parentRenderGroup; if (g) g.structureDidChange = true; };
export const ART_B = Q.get('art') !== 'a';
/** a dev readout of the frame interval (median, p95 over the last 2 s) for the real-phone read: ?perf=1 */
export const PERF = Q.get('perf') === '1';
/** dev: the capture hooks tools/art/paint_capture.mjs drives (?paintcap=1); never in a player's URL */
export const PAINTCAP = Q.get('paintcap') === '1';
export const RES = 3;
/** studio 011 (#41): the painted ground plates are retired; the street's ground is the ground kit's (groundkit.ts), drawn
 *  from the world's cells. ?plate=1 draws the plates again for comparison until the blind gate passes (then this goes) */
export const PLATE = Q.get('plate') === '1';
/** dev: force one painted set (?set=day|night|snow) for the shot sets; otherwise the hour and the weather choose */
const FORCE_SET = Q.get('set');
/** the hard cap on painted texture bytes resident on the GPU (the LRU never loads past it). 40 MB since studio 009: with
 *  eight painted blocks a phone's view (plus its margin and prefetch) wanted more than 24 MB at the Tavern, w105, n1300 and
 *  wb, and the tiles it could not load kept today's art (tools/art/paint_mem_walk.mjs: refused 157 / 443 frame-counts day /
 *  snow at 24, 19 / 24 at 32, 0 / 0 at 40). Block-compressed, 1 byte a texel: 40 MB is small for a phone's GPU */
export const CAP_BYTES = (Number(Q.get('paintcap_mb')) || 40) * 1048576;
const LOAD_TIMEOUT = 20000;
/** studio 009: the least share of a prop's rect its props tile must cover for the prop to take the painting */
const COV_MIN = 0.05;
/** #27: logical px round a broken prop's strips that its painting may still cover (the painting runs past the engine's
 *  sprite: a painted table top stood ~10 px over its strips' rect) */
const GHOST_PAD = 12;
const IDLE_MS = 20000;

/** what became of the painted block's load, for the ?perf=1 readout (a phone has no console) */
export let paintState = 'loading';

/** the KTX2 transcoder: Pixi's own worker code, started from /ktx/ktx.worker.js (client/ktxworker.mjs writes it), not from
 *  a blob: URL. A blob worker takes the page's CSP, and libktx's embind glue needs eval, which the page refuses; a worker
 *  from its own URL takes the policy served with its script (the vhost allows eval for /ktx/ only). Started only when the painted
 *  block first needs it; a worker that fails to start fails every load (counted by the callers), never hangs one */
let ktxW: Worker | null = null, ktxDead = '';
const ktxPend = new Map<string, { res: (o: any) => void; rej: (e: Error) => void }>();
async function ktxWorker(): Promise<Worker> {
  if (ktxW) return ktxW;
  const fmts = await getSupportedTextureFormats();
  const w = new Worker('/ktx/ktx.worker.js');
  w.onmessage = (ev: MessageEvent) => {
    const { err, url, textureOptions, type } = ev.data ?? {};
    if (type === 'error' || err) {
      const e = new Error(String(err?.message ?? err ?? 'ktx worker error'));
      if (url && ktxPend.has(url)) { ktxPend.get(url)!.rej(e); ktxPend.delete(url); }
      else { ktxDead = e.message; for (const p of ktxPend.values()) p.rej(e); ktxPend.clear(); }   // (init failed: nothing will load)
      return;
    }
    const p = ktxPend.get(url); if (p) { ktxPend.delete(url); p.res(textureOptions); }
  };
  w.onerror = (ev) => { ktxDead = 'worker: ' + (ev.message || 'failed to start'); for (const p of ktxPend.values()) p.rej(new Error(ktxDead)); ktxPend.clear(); };
  w.postMessage({ type: 'init', jsUrl: '/ktx/libktx.js', wasmUrl: '/ktx/libktx.wasm', supportedTextures: fmts });
  return (ktxW = w);
}
/** one KTX2 file as a texture (its size in texels: the caller sets the resolution) */
export async function loadKtx(url: string): Promise<Texture> {
  const w = await ktxWorker();
  if (ktxDead) throw new Error(ktxDead);
  const abs = new URL(url, location.href).href;
  const opts = await new Promise<any>((res, rej) => { ktxPend.set(abs, { res, rej }); w.postMessage({ type: 'load', url: abs }); });
  return new Texture({ source: new CompressedSource({ ...opts, resolution: 1 }) });
}

interface Ent { x: number; y: number; w: number; h: number; tw: number; th: number;
  /** studio 009, a props tile: its coverage mask (a cell every cs logical px is set where the texture has pixels) */
  cov?: string; cs?: number }
interface TileDef { x: number; y: number; plate?: Ent; walls?: Ent; props?: Ent; roofs?: (Ent & { b: number })[];
  /** studio 006, the light pack (paint_b.py lightpack): lit windows added over the walls, their light (and the spill on the
   *  pavement) drawn into the light map; every set has its own copy (a set loads as one unit) */
  emit?: Ent; lite?: Ent }
interface Manifest {
  v: number; res: number; roof_res: number; rect: [number, number, number, number]; blds: number[];
  sets: Record<string, { dir: string; enc: string; gpu: number }>;
  tiles: Record<string, TileDef>;
  /** studio 006: wall cell "cx,cy" -> its tile, and the outer columns on the rect that keep today's art, with why */
  cols?: Record<string, string>; colmiss?: Record<string, string>;
  foot: { file: string; x: number; y: number; w: number; h: number };
  probe: Record<string, { file: string; x: number; y: number; w: number; h: number; step: number }>;
  lightpack?: { windows: number; lit: number; res: { emit: number; lite: number } };
  /** issue #9 (proto-room): the props painting's prop list, type@cell (paint_b.py does not emit it yet: absent = every prop) */
  prop_keys?: string[];
}

/** one page of masked slices at RES, packed on shelves; a texture per slice in logical units (the source carries RES) */
export class SliceAtlas {
  pages: { cv: HTMLCanvasElement; g: CanvasRenderingContext2D; src: CanvasSource; x: number; y: number; row: number; dirty: boolean }[] = [];
  private scratch = document.createElement('canvas');
  bytes = 0;
  /** paint(g, w, h) draws the slice into a w x h (device texel) scratch canvas; returns a texture w/RES x h/RES */
  add(w: number, h: number, paint: (g: CanvasRenderingContext2D) => void): Texture {
    const P = 1024;
    let p = this.pages[this.pages.length - 1];
    if (p && p.x + w + 2 > P) { p.x = 0; p.y += p.row + 2; p.row = 0; }
    if (!p || p.y + h + 2 > P) {
      const cv = document.createElement('canvas'); cv.width = cv.height = P;
      const g = cv.getContext('2d')!;
      const src = new CanvasSource({ resource: cv, resolution: RES, scaleMode: 'linear' });
      p = { cv, g, src, x: 0, y: 0, row: 0, dirty: false }; this.pages.push(p); this.bytes += P * P * 4;
    }
    const s = this.scratch; if (s.width < w || s.height < h) { s.width = Math.max(s.width, w); s.height = Math.max(s.height, h); }
    const g = s.getContext('2d')!; g.setTransform(1, 0, 0, 1, 0, 0); g.globalCompositeOperation = 'source-over'; g.clearRect(0, 0, w, h);
    paint(g);
    p.g.drawImage(s, 0, 0, w, h, p.x, p.y, w, h);
    const t = new Texture({ source: p.src, frame: new Rectangle(p.x / RES, p.y / RES, w / RES, h / RES) });
    p.x += w + 2; p.row = Math.max(p.row, h); p.dirty = true;
    return t;
  }
  flush() { for (const p of this.pages) if (p.dirty) { p.src.update(); p.dirty = false; } }
}
/** one tile in one set: its textures by kind ('plate', 'walls', 'props', 'roof:<b>') */
interface TS { key: string; set: string; state: 'loading' | 'ok' | 'failed'; tex: Map<string, Texture>; bytes: number; used: number }
interface ColUser { spr: Sprite; kind: 'walls' | 'props'; key: string; x0: number; y0: number; w: number; h: number; orig: Texture; dmg: boolean;
  frames: Map<string, Texture>; base: string; ov: Sprite | null; ovSet: string; em: Sprite | null;
  /** a prop strip's own transform (a painted strip is drawn at scale 1 from its screen rect's top-left, mirrored or not) */
  tf: { ax: number; ay: number; sx: number; sy: number; dx: number; dy: number; painted: boolean } | null;
  /** #27: the prop a strip belongs to (iso_render ColRec.grp; 0 for a cell column such as a railing), and whether it is back
   *  on today's art because a broken prop's painting is in its frame */
  grp: number; back: boolean;
  /** occlusion round 2: a wall column the painting does not cover borrows a painted neighbour's slice of the same face, this
   *  far off on screen (logical px) */
  bx?: number; by?: number }
interface Box { x0: number; y0: number; x1: number; y1: number }
interface PieceUser { key: string; kind: string; e: Ent; spr: Sprite; ov: Sprite; base: string; ovSet: string; roof?: Sprite; fe?: Filter[];
  /** what the piece's alpha is multiplied by (the windows' light for the light map's pieces; 1 for the plate and the roofs) */
  k?: () => number }

/** occlusion round 2: a roof piece's alpha ramps from 0 at its rectangle's edge to 1 FEATHER_PX device px in, on the edges
 *  given (uE: left, top, right, bottom; 1 = fade). Only an edge that no other piece of the same roof continues fades: two
 *  pieces meeting both faded showed the engine roof as a dark cross between them (r2b) */
const featherProg = () => new GlProgram({ name: 'dm-feather', vertex: defaultFilterVert, fragment: `
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform highp vec4 uInputSize;
uniform highp vec4 uOutputFrame;
uniform highp vec4 uE;
void main(void) {
  vec4 c = texture(uTexture, vTextureCoord);
  vec2 p = vTextureCoord * uInputSize.xy, sz = uOutputFrame.zw;
  float e = 1e6;
  if (uE.x > 0.5) e = min(e, p.x); if (uE.y > 0.5) e = min(e, p.y); if (uE.z > 0.5) e = min(e, sz.x - p.x); if (uE.w > 0.5) e = min(e, sz.y - p.y);
  finalColor = c * smoothstep(0.0, 20.0, e);
}` });
let featherP: GlProgram | null = null;
const featherFor = (edges: number[]) => new Filter({ glProgram: featherP ??= featherProg(), resources: { fe: { uE: { value: new Float32Array(edges), type: 'vec4<f32>' } } } });
const GL1 = (() => { try { return !document.createElement('canvas').getContext('webgl2'); } catch { return true; } })();
const tileOf = (x: number, y: number) => { const r = Math.floor(y / 256), n = Math.floor((x / 256 + 1) / 2); return `${n + r}_${r - n}`; };
const DMG_TINT = 0xb4a89c;

export class PaintBlock {
  m: Manifest | null = null;
  plate = new Container();
  /** occlusion round 2: painted building -> [r, g, b, spread] of its painted roof (rooftone.json) */
  roofTone = new Map<number, number[]>();
  /** occlusion round 2: wall columns drawn with a painted neighbour's slice (the painting covered none of their face) */
  borrowed = 0;
  foot = new Sprite();
  /** the light map's share of the light pack (lit windows and their spill), drawn into the light RT at -cam */
  liteLayer = new Container();
  /** 0 by day .. 1 at night: the windows' lights, on the dark ramp (studio 006: the night is the engine's) */
  lightsOn = 0;
  /** studio 006: the season's set (snow from November to March and whenever it snows, else day), swapped whole; the set it
   *  replaced stays as the fallback until every tile the view wants is resident in the new one, then unloads */
  cur = ''; private prev = ''; private swapT = 0; private curReady = false;
  /** the painted tiles on screen are still streaming in after a camera jump (main.ts: the loading cover stays up) */
  busy = false; private jumpT = 0;
  /** the original texture of every painted sprite (the cutaway's alpha tests read it: iso_render ALPHA_PROXY) */
  orig = new WeakMap<Texture, Texture>();
  /** issue #9: every column registered as a painted outer wall (iso_render: the interior kit leaves those faces alone) */
  painted = new WeakSet<Sprite>();
  ok = false;
  private sets = new Map<string, TS>();                 // `${key}|${set}`
  private users = new Map<string, Set<ColUser>>();       // tile key -> painted columns
  private pieces: PieceUser[] = [];                       // plate tiles and roof pieces
  private roofsDone = new Set<number>();
  private bldSet = new Set<number>();
  private probeLum: Float32Array | null = null; private probeW = 0; private probeH = 0; private probeM: Manifest['probe'][string] | null = null;
  /** the painted sets to show, base first: [day] | [day, night at a] | [night] */
  layers: { set: string; a: number }[] = [];
  nightShare = 0;
  failed = 0; failedKeys: string[] = []; capped = 0; dmgCols = new Set<Sprite>();
  format = '?';
  private view = { x: 0, y: 0, w: 0, h: 0 }; private vel = { x: 0, y: 0 }; private lastC: [number, number] | null = null; private tick = 0; private syncTick = 0;
  private base = 'art/b/';
  /** studio 009: each props tile's coverage (paint_b.py: a cell every cs logical px, set where the texture has pixels) */
  private cov = new Map<string, { w: number; h: number; cs: number; d: Uint8Array }>();
  /** #27: broken painted props' screen rects (their strips' union, padded), by prop id; and the props sent back to today's
   *  art for them (by prop id; a cell column, grp 0, goes back on its own) */
  private ghosts = new Map<number, Box>();
  private backGrps = new Set<number>();

  async load(base = 'art/b/') {
    this.base = base;
    try {
      const r = await fetch(base + 'stream.json'); if (!r.ok) { paintState = 'no stream.json ' + r.status; return; }
      const m: Manifest = await r.json(); this.m = m;
      for (const b of m.blds ?? []) this.bldSet.add(b);
      const fmts = await getSupportedTextureFormats();
      this.format = (['bc7-rgba-unorm', 'astc-4x4-unorm', 'etc2-rgba8unorm', 'bc3-rgba-unorm'].find(f => fmts.includes(f as any)) ?? 'rgba8unorm').replace(/-.*/, '');
      // the footprint (1x, alpha): where the light RT reaches 1.0 by the night set's share (a small PNG: no canvas copy)
      const ft = await Assets.load<Texture>({ src: base + m.foot.file, data: { scaleMode: 'linear' } });
      ft.source.scaleMode = 'linear'; this.foot.texture = ft; this.foot.width = m.foot.w; this.foot.height = m.foot.h;
      // the props tiles' coverage, read once (small PNGs): propFits needs it when the chunks are built
      await Promise.all(Object.entries(m.tiles).filter(([, t]) => t.props?.cov).map(async ([key, t]) => {
        const im = new Image(); im.src = base + t.props!.cov!; await im.decode().catch(() => {});
        if (!im.naturalWidth) return;
        const cv = document.createElement('canvas'); cv.width = im.naturalWidth; cv.height = im.naturalHeight;
        const g = cv.getContext('2d', { willReadFrequently: true })!; g.drawImage(im, 0, 0);
        const px = g.getImageData(0, 0, cv.width, cv.height).data, d = new Uint8Array(cv.width * cv.height);
        for (let i = 0; i < d.length; i++) d[i] = px[i * 4] > 127 ? 1 : 0;
        this.cov.set(key, { w: cv.width, h: cv.height, cs: t.props!.cs ?? 2, d });
      }));
      // the plate pieces: a sprite per tile, its texture when the tile streams in
      if (PLATE) for (const [key, t] of Object.entries(m.tiles)) if (t.plate) this.pieces.push(this.piece(key, 'plate', t.plate, this.plate));
      // the windows' light and their spill: sprites in the light map (iso_render renderLight adds this layer to its RT)
      for (const [key, t] of Object.entries(m.tiles)) if (t.lite) { const p = this.piece(key, 'lite', t.lite, this.liteLayer); p.spr.blendMode = 'add'; p.k = () => this.lightsOn; this.pieces.push(p); }
      await this.loadProbe().catch(e => console.warn('paintblock: no probe', e));
      // occlusion round 2: each painted building's roof tone (tools/art/rooftone.py), for the engine roof beside its pieces
      try { const r = await fetch(base + 'rooftone.json'); if (r.ok) for (const [k, v] of Object.entries((await r.json()).tone ?? {})) this.roofTone.set(+k, v as number[]); } catch { /* the engine's own membrane */ }
      this.ok = true; paintState = 'painted';
    } catch (e) { console.warn('paintblock: not loaded', e); this.ok = false; paintState = 'FAILED ' + String((e as Error)?.message ?? e).slice(0, 60); }
  }

  private piece(key: string, kind: string, e: Ent, parent: Container, roof?: Sprite): PieceUser {
    const spr = new Sprite(); spr.x = e.x; spr.y = e.y; spr.visible = false;
    const ov = new Sprite(); ov.x = e.x; ov.y = e.y; ov.visible = false;
    if (roof) { spr.zIndex = roof.zIndex + 0.001; ov.zIndex = roof.zIndex + 0.002; }
    // a ground plate is drawn 1 logical px bigger (0.5 each side), so neighbours overlap their edges: a tile is padded with
    // TRANSPARENT texels to whole KTX blocks, and filtering (worse on a mip level, a desktop's scaled view) half-fades its
    // outermost row, which drew a light hairline of the engine's ground along every tile edge (2026-10-29 desktop play)
    if (kind === 'plate' && e.w > 2 && e.h > 2) for (const s of [spr, ov]) { s.scale.set((e.w + 1) / e.w, (e.h + 1) / e.h); s.x = e.x - 0.5; s.y = e.y - 0.5; }
    parent.addChild(spr, ov);
    return { key, kind, e, spr, ov, base: '', ovSet: '', roof };
  }

  hasBld(id: number) { return this.ok && this.bldSet.has(id); }
  /** whether the props painting shows this prop (the manifest's prop_keys, type@cell): one placed since it was painted (the map
   *  designer's benches) must keep its own sprite, not a slice of whatever the painting had there */
  private propKeys: Set<string> | null = null;
  knowsProp(p: { t: string; x: number; y: number }): boolean {
    if (!this.m?.prop_keys) return true;
    if (!this.propKeys) this.propKeys = new Set(this.m.prop_keys);
    return this.propKeys.has(`${p.t}@${p.x},${p.y}`);
  }

  /** the tile a sprite's foot stands in, if its texture of `kind` holds the sprite's whole screen rect */
  private tileFor(kind: 'walls' | 'props', x0: number, y0: number, w: number, h: number): string | null {
    // the tile of the sprite's foot first; a sprite whose frame runs past its painted pixels (its foot a few px into a
    // tile with no walls, cycle 006's alley faces) takes the tile above or beside it that holds its whole rect
    const cx = x0 + w / 2, fy = y0 + h - 0.5;
    for (const [px, py] of [[cx, fy], [cx, fy - 16], [cx, fy - 32], [cx - 16, fy - 8], [cx + 16, fy - 8]]) {
      const key = tileOf(px, py), e = this.m?.tiles[key]?.[kind];
      if (e && x0 >= e.x - 1e-6 && y0 >= e.y - 1e-6 && x0 + w <= e.x + e.w + 1e-6 && y0 + h <= e.y + e.h + 1e-6) return key;
    }
    return null;
  }
  private rectOf(spr: Sprite) {
    const f = spr.texture.frame, kx = spr.scale.x, ky = spr.scale.y;
    const ax = spr.x - spr.anchor.x * f.width * kx, ay = spr.y - spr.anchor.y * f.height * ky;
    return { x0: Math.min(ax, ax + f.width * kx), y0: Math.min(ay, ay + f.height * ky), w: Math.abs(f.width * kx), h: Math.abs(f.height * ky) };
  }

  /** studio 004 (Matt, 2026-10-20: props must fit smoothly): a prop takes the painting on ALL of its strips or on none */
  propFits(sprs: Sprite[]): boolean {
    if (!this.ok) return false;
    // studio 009: and the tile must hold painting there. A tile whose box holds a prop it has no pixels of (the prop was
    // left to a block that does not paint it, or its block's props layer was under the fit floor) drew an EMPTY frame: an
    // invisible bench you walk into. Under COV_MIN of the prop's rect covered, it keeps today's art
    let on = 0, all = 0;
    for (const spr of sprs) {
      const r = this.rectOf(spr), key = this.tileFor('props', r.x0, r.y0, r.w, r.h);
      if (!key) return false;
      const c = this.cov.get(key), e = this.m!.tiles[key].props!;
      if (!c) continue;
      const i0 = Math.max(0, Math.floor((r.x0 - e.x) / c.cs)), i1 = Math.min(c.w, Math.ceil((r.x0 + r.w - e.x) / c.cs));
      const j0 = Math.max(0, Math.floor((r.y0 - e.y) / c.cs)), j1 = Math.min(c.h, Math.ceil((r.y0 + r.h - e.y) / c.cs));
      for (let j = j0; j < j1; j++) for (let i = i0; i < i1; i++) { all++; on += c.d[j * c.w + i]; }
    }
    if (all && on / all < COV_MIN) { this.emptyProps++; return false; }
    return true;
  }
  /** props that kept today's art because their tile holds no painting of them (the readout: empty) */
  emptyProps = 0;

  /** iso_render's hook for every column it builds. A painted building's OUTER wall (its south or east face is outside: the
   *  faces the camera sees; interiors keep today's art) takes the walls tile its foot is in, through a texture frame at its
   *  own screen rect; an outdoor prop strip takes the props tile's. The sprite is reset to scale 1 at its screen rect's
   *  top-left (a mirrored strip included: the tiles are in screen orientation); its depth, band and cutaway are untouched.
   *  A damaged wall keeps its painting, darkened (counted in the readout) */
  column(spr: Sprite, bld: number, damaged: boolean, prop: boolean, outer = false, cx = -1, cy = -1, grp = 0, edge = false) {
    if (!this.ok) return;
    if (!prop && (!this.bldSet.has(bld) || spr.scale.x !== 1 || spr.scale.y !== 1)) return;
    // (occlusion round 2: a painted building's wall that is not an outer face - a party wall rising above a lower neighbour,
    // w106b_1's grey slab - is seen too; it borrows like an uncovered outer column, below, and is otherwise left as it was)
    const inner = !prop && !outer;
    if (inner) return;   // (a wall that is not an outer face keeps today's art; edge is kept for a later rule)
    void edge;
    const r = this.rectOf(spr), kind = prop ? 'props' : 'walls';
    // a wall column takes the tile its own cell's painted pixels are in (paint_b colmap, from the engine's face map), with
    // no frame test: the frame may run past the texture, which is transparent at its edge (sampling clamps); a column the
    // manifest names in colmiss keeps today's art for the reason given there (the readout counts and names them)
    const ck = `${cx},${cy}`;
    let key = inner ? null : prop || !this.m!.cols ? this.tileFor(kind, r.x0, r.y0, r.w, r.h) : (this.m!.cols[ck] ?? null);
    let bx = 0, by = 0;
    // (only the colmiss outer faces: borrowing for unmapped and party walls too, r2d, laid whole facades over roofs - wd_2,
    // tavern_2, w105_2)
    if (!key && !prop && !inner && this.m!.colmiss?.[ck]) {
      // occlusion round 2 (blind A/B, w106b_1 / we_1: 'untextured', 'unfinished' - 1040 columns the painting covers 0% of
      // kept the engine's flat face): borrow the nearest painted column of the SAME face (the run of wall cells it lies in,
      // along x for a south face or y for an east face), its slice shifted on screen by the cells between them
      const C = this.m!.cols ?? {}, M = this.m!.colmiss ?? {}, wall = (x: number, y: number) => !!(C[`${x},${y}`] || M[`${x},${y}`]);
      const alongX = wall(cx - 1, cy) || wall(cx + 1, cy), alongY = wall(cx, cy - 1) || wall(cx, cy + 1);
      const dirs: [number, number][] = alongX && !alongY ? [[1, 0], [-1, 0]] : alongY && !alongX ? [[0, 1], [0, -1]] : [[1, 0], [-1, 0], [0, 1], [0, -1]];
      for (let k = 1; k <= 3 && !key; k++) for (const [dx, dy] of dirs) {   // (a gap of up to 3 columns: across a long run every column repeated its neighbour's edge slice, a ribbed wall with no windows - tavern_2, w105_2, r3)
        const nk = C[`${cx + dx * k},${cy + dy * k}`]; if (!nk) continue;
        key = nk; bx = (dx - dy) * k * 8; by = (dx + dy) * k * 4; break;
      }
      if (!key) { if (!this.missSeen.has(ck)) { this.missSeen.add(ck); this.missCols++; if (this.missEx.length < 8) this.missEx.push(ck + ' ' + M[ck]); } return; }
      this.borrowed++;
    }
    if (!key) return;
    const u: ColUser = { spr, kind, key, ...r, orig: spr.texture, dmg: damaged && !prop, frames: new Map(), base: '', ov: null, ovSet: '', em: null,
      tf: prop ? { ax: spr.anchor.x, ay: spr.anchor.y, sx: spr.scale.x, sy: spr.scale.y, dx: spr.x - r.x0, dy: spr.y - r.y0, painted: false } : null,
      grp, back: false, bx, by };
    let s = this.users.get(key); if (!s) this.users.set(key, s = new Set()); s.add(u);
    if (prop && this.ghosts.size) {   // (#27: a strip built beside a broken prop, or one of a prop already sent back)
      if (grp && !this.backGrps.has(grp) && this.nearGhost(u)) { this.backGrps.add(grp); this.regroup(); }
      u.back = grp ? this.backGrps.has(grp) : this.nearGhost(u);
    }
    if (!prop) this.painted.add(spr);
    if (u.dmg) this.dmgCols.add(spr);
    this.applyCol(u);
  }

  /** a painted building's roof: its pieces (one per tile it crosses, baked masked to the roof's own shape at ROOF_RES), drawn
   *  over every figure; their alpha follows the roof (the cutaway fades both) */
  roof(bid: number, roof: Sprite, parent: Container) {
    if (!this.ok || !this.m || !this.bldSet.has(bid) || this.roofsDone.has(bid)) return;
    this.roofsDone.add(bid);
    for (const [key, t] of Object.entries(this.m.tiles)) for (const e of t.roofs ?? []) if (e.b === bid) this.pieces.push(this.piece(key, 'roof:' + bid, e, parent, roof));
  }

  // ------------------------------------------------------------------ which set, which texture
  private ts(key: string, set: string) { const t = this.sets.get(key + '|' + set); return t && t.state === 'ok' ? t : null; }
  /** base and overlay sets for a tile, from the layers: the first layer that is resident is the base, the next resident one
   *  above it the overlay (so a set still streaming in never shows today's art where the other set is already there) */
  private pick(key: string): [string, string, number] {
    let b = '', o = '', a = 0;
    for (const L of this.layers) { if (!this.ts(key, L.set)) continue; if (!b) b = L.set; else if (!o && L.a > 0) { o = L.set; a = L.a; } }
    return [b, o, a];
  }
  private frameFor(u: ColUser, set: string, kind: 'walls' | 'props' | 'emit' = u.kind): Texture | null {
    const t = this.ts(u.key, set)?.tex.get(kind); if (!t) return null;
    const fk = kind === u.kind ? set : set + ':' + kind;
    let f = u.frames.get(fk);
    if (!f || f.source !== t.source) {
      f?.destroy(false);
      const e = this.m!.tiles[u.key][kind]!;
      f = new Texture({ source: t.source, frame: new Rectangle(u.x0 + (u.bx ?? 0) - e.x, u.y0 + (u.by ?? 0) - e.y, u.w, u.h) });
      this.orig.set(f, u.orig); u.frames.set(fk, f);
      ALPHA_PROXY.set(f, { tex: u.orig, fx: !!u.tf && u.tf.sx < 0, fy: !!u.tf && u.tf.sy < 0 });
    }
    return f;
  }
  private applyCol(u: ColUser) {
    if (u.spr.destroyed) return;
    const [b, o] = u.back ? ['', ''] : this.pick(u.key);
    if (b !== u.base) {
      const f = b ? this.frameFor(u, b) : null;
      const T = u.tf, sp = u.spr;
      if (T && !!f !== T.painted) {   // (a prop strip: the painting is in screen orientation, today's art in its own)
        if (f) { sp.anchor.set(0, 0); sp.scale.set(1, 1); sp.x -= T.dx; sp.y -= T.dy; }
        else { sp.anchor.set(T.ax, T.ay); sp.scale.set(T.sx, T.sy); sp.x += T.dx; sp.y += T.dy; }
        T.painted = !!f;
      }
      sp.texture = f ?? u.orig; u.base = b; rebuild(sp);
      sp.tint = f && u.dmg ? DMG_TINT : 0xffffff;
    }
    if (o !== u.ovSet) {
      u.ovSet = o;
      if (o) { const f = this.frameFor(u, o)!; if (!u.ov) { u.ov = new Sprite(f); } else { u.ov.texture = f; rebuild(u.ov); } u.ov.visible = false; }
      else if (u.ov) { u.ov.destroy(); u.ov = null; }
    }
    // the lit windows over a painted wall column: its frame of the tile's emit layer, added (frame() keeps it on its column)
    const ef = u.kind === 'walls' && b && this.m!.tiles[u.key].emit ? this.frameFor(u, b, 'emit') : null;
    if (ef) { if (!u.em) { u.em = new Sprite(ef); u.em.blendMode = 'add'; u.em.visible = false; } else if (u.em.texture !== ef) { u.em.texture = ef; rebuild(u.em); } }
    else if (u.em) { u.em.destroy(); u.em = null; }
  }
  // ------------------------------------------------------------------ broken props (#27)
  // A prop strip's frame is its whole screen rect of the props tile, and the tile is ONE painting of every prop (paint_b
  // paints the props layer as one image), so a strip draws its neighbours' painting too. While they stand it lands where
  // they draw it; once one is broken the strips round it went on drawing it (a ghost table over the debris). Clipping a
  // strip to its own silhouette does not cure it (tried: the painted chairs and the engine's railing panel overlap the
  // painted table top, so the wood stayed inside their silhouettes), and the painting has no per-prop ownership to clip by.
  // So a painted prop whose strips meet a broken prop's padded rect goes back to today's art, all its strips or none (the
  // propFits rule): no ghost, the old look only round a broken prop, and it comes back when the prop is mended.
  private nearGhost(u: ColUser): boolean {
    for (const [id, g] of this.ghosts) if (id !== u.grp && g.x0 < u.x0 + u.w && g.x1 > u.x0 && g.y0 < u.y0 + u.h && g.y1 > u.y0) return true;
    return false;
  }
  /** iso_render.propDied / propMended: a broken painted prop's strips (the sprites propSlices made for it: their rects), or
   *  null when it stands again */
  ghost(id: number, sprs: Sprite[] | null) {
    if (!this.ok) return;
    if (sprs) {
      if (!sprs.length) return;
      const g: Box = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
      for (const s of sprs) { const r = this.rectOf(s); g.x0 = Math.min(g.x0, r.x0); g.y0 = Math.min(g.y0, r.y0); g.x1 = Math.max(g.x1, r.x0 + r.w); g.y1 = Math.max(g.y1, r.y0 + r.h); }
      g.x0 -= GHOST_PAD; g.y0 -= GHOST_PAD; g.x1 += GHOST_PAD; g.y1 += GHOST_PAD;
      this.ghosts.set(id, g);
    } else if (!this.ghosts.delete(id)) return;
    this.backGrps.clear();
    for (const s of this.users.values()) for (const u of s) if (u.kind === 'props' && u.grp && !u.spr.destroyed && this.nearGhost(u)) this.backGrps.add(u.grp);
    this.regroup();
  }
  /** every painted prop strip on today's art or the painting, by the broken props (only the strips that change are redone) */
  private regroup() {
    for (const s of this.users.values()) for (const u of s) {
      if (u.kind !== 'props' || u.spr.destroyed) continue;
      const back = u.grp ? this.backGrps.has(u.grp) : this.nearGhost(u);
      if (back !== u.back) { u.back = back; this.applyCol(u); }
    }
  }
  /** strips on today's art round broken props (the readout: back) */
  backCount() { let n = 0; for (const s of this.users.values()) for (const u of s) if (u.back && !u.spr.destroyed) n++; return n; }

  private applyPiece(p: PieceUser) {
    const [b, o, a] = this.pick(p.key);
    const tex = (s: string) => this.ts(p.key, s)?.tex.get(p.kind) ?? null;
    if (b !== p.base) { p.base = b; const t = b ? tex(b) : null; if (t) { p.spr.texture = t; rebuild(p.spr); } }
    if (o !== p.ovSet) { p.ovSet = o; const t = o ? tex(o) : null; if (t) { p.ov.texture = t; rebuild(p.ov); } }
    const on = !!p.base && (!p.roof || (p.roof.visible && p.roof.alpha > 0.01 && !p.roof.destroyed));
    const ra = p.roof ? p.roof.alpha : 1;
    const k = p.k ? p.k() : 1;
    p.spr.visible = on && k > 0.005; p.spr.alpha = ra * k;
    p.ov.visible = on && !!p.ovSet && a > 0.01 && k > 0.005; p.ov.alpha = ra * a * k;
    // cycle 011: and its window (roofHole) - without it a hero under a painted roof was only the x-ray outline
    // occlusion round 2 (blind A/B: 'hard-edged roof rectangles where painted roof stops on flat grey engine roof'): a roof piece
    // ends on its TILE's straight edge wherever the roof runs on past the painted block; it now fades out over its last
    // ~20 device px onto the engine roof beneath, which carries the painted roof's tone (rooftone.json)
    if (p.roof) {
      if (!p.fe) { const e = p.e, mine = this.pieces.filter(q => q !== p && q.kind === p.kind), T = 2;
        const touches = (side: number) => mine.some(q => { const f = q.e; const ov = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0) > 4;
          return side === 0 ? Math.abs(f.x + f.w - e.x) < T && ov(e.y, e.y + e.h, f.y, f.y + f.h) : side === 2 ? Math.abs(e.x + e.w - f.x) < T && ov(e.y, e.y + e.h, f.y, f.y + f.h)
            : side === 1 ? Math.abs(f.y + f.h - e.y) < T && ov(e.x, e.x + e.w, f.x, f.x + f.w) : Math.abs(e.y + e.h - f.y) < T && ov(e.x, e.x + e.w, f.x, f.x + f.w); });
        p.fe = [featherFor([0, 1, 2, 3].map(sd => touches(sd) ? 0 : 1))]; }
      const F = p.roof.filters?.length ? p.roof.filters as any : p.fe; if (p.spr.filters?.[0] !== (F as any)[0]) { p.spr.filters = F; p.ov.filters = F; }
    }
  }

  // ------------------------------------------------------------------ streaming
  /** main.ts, every frame: the camera rect (world-screen logical px), the night share (0 day .. 1 night, from the existing
   *  dark ramp) and whether it snows. Chooses the layers, streams, and keeps the overlays on their columns */
  frame(camX: number, camY: number, vw: number, vh: number, dark: number, snow: boolean, power = true) {
    if (!this.ok || !this.m) return;
    const S = this.m.sets, has = (s: string) => !!S[s];
    // cycle 011: the snow set only while it snows. It was every day Nov-Mar (the trees' month rule), but the painted blocks are
    // a third of the merged street and the engine's ground and roofs have no snow (a code-drawn cover was withdrawn in
    // 9f39538), so from Nov 1 each painted block would sit white in a bare street, ending hard at its edges
    // 2026-10-31 night (Matt: "your painting doesn't line up ... half the rooftops are messed up", in real snow): the snow set
    // is off until the core blocks are repainted on today's world; the snow edit made their mismatch with the world far worse
    void snow;
    const dayish = (FORCE_SET && has(FORCE_SET)) ? FORCE_SET : has('day') ? 'day' : has('night') ? 'night' : Object.keys(S)[0];
    if (dayish !== this.cur) { this.prev = this.cur; this.cur = dayish; this.swapT = performance.now(); this.curReady = false; }
    // the old set is a layer of weight 0 until the new one is ready (or 15 s): never today's art in between, never both drawn
    const keepPrev = !!this.prev && !this.curReady && performance.now() - this.swapT < 15000;
    if (!keepPrev) this.prev = '';
    const f = Math.max(0, Math.min(1, (dark - 0.35) / 0.4)), nf = f * f * (3 - 2 * f);
    let L: { set: string; a: number }[];
    if (!has('night') || dayish === 'night') L = [{ set: dayish, a: 1 }];
    else if (nf <= 0.001) L = [{ set: dayish, a: 1 }];
    else if (nf >= 0.999) L = [{ set: 'night', a: 1 }];
    else L = [{ set: dayish, a: 1 }, { set: 'night', a: nf }];
    if (keepPrev && !L.some(l => l.set === this.prev)) L.push({ set: this.prev, a: 0 });
    const lk = L.map(l => l.set + l.a.toFixed(2)).join(',');
    const changed = lk !== this.layers.map(l => l.set + l.a.toFixed(2)).join(',');
    this.layers = L;
    // the footprint's light: the night set carries its own light, so the light RT is 1.0 there by the night share
    const nightOnly = L.length === 1 && L[0].set === 'night';
    this.nightShare = FORCE_SET ? (FORCE_SET === 'night' ? 1 : 0) : nightOnly ? 1 : L.length === 2 ? L[1].a : 0;
    this.foot.alpha = this.nightShare;
    // the night is the engine's: the windows light along the same dark ramp the light map's ambient follows (one
    // continuous blend through dusk and dawn)
    { (this as any).lastDark = dark; const g = Math.max(0, Math.min(1, (dark - 0.2) / 0.5)); this.lightsOn = power ? g * g * (3 - 2 * g) : 0; }   // (a power cut puts the windows out with the lamps)

    // camera motion (logical px a frame, smoothed) for the prefetch
    if (this.lastC) { const dx = camX - this.lastC[0], dy = camY - this.lastC[1]; if (Math.hypot(dx, dy) < 200) { this.vel.x = this.vel.x * 0.9 + dx * 0.1; this.vel.y = this.vel.y * 0.9 + dy * 0.1; } else this.jumpT = performance.now(); }
    else this.jumpT = performance.now();
    this.lastC = [camX, camY]; this.view = { x: camX, y: camY, w: vw, h: vh };
    if (changed || ++this.tick % 6 === 0) this.stream();
    if (changed) this.reapply();
    // overlays follow their columns (alpha, visibility, the cutaway's filters, the band they are in)
    const a = L.length === 2 ? L[1].a : 0;
    const lo = this.lightsOn;
    for (const s of this.users.values()) for (const u of [...s]) {
      const sp = u.spr;
      if (sp.destroyed) { this.retire(s, u); continue; }   // (its overlays go with it NOW: see retire)
      if (u.em) {
        if (u.em.parent !== sp.parent) { u.em.parent?.removeChild(u.em); if (sp.parent) sp.parent.addChild(u.em); }
        u.em.zIndex = sp.zIndex + 2e-4; u.em.x = sp.x; u.em.y = sp.y;
        u.em.visible = lo > 0.01 && sp.visible && sp.texture !== u.orig && sp.alpha > 0.01; u.em.alpha = sp.alpha * lo;
        if (u.em.filters !== sp.filters) u.em.filters = sp.filters as any;
      }
      if (!u.ov) continue;
      if (u.ov.parent !== sp.parent) { u.ov.parent?.removeChild(u.ov); if (sp.parent) { sp.parent.addChild(u.ov); } }
      u.ov.zIndex = sp.zIndex + 1e-4; u.ov.x = sp.x; u.ov.y = sp.y; u.ov.tint = sp.tint;
      u.ov.visible = sp.visible && sp.texture !== u.orig && sp.alpha > 0.01; u.ov.alpha = sp.alpha * a;
      if (u.ov.filters !== sp.filters) u.ov.filters = sp.filters as any;
    }
    for (const p of this.pieces) this.applyPiece(p);
  }

  private reapply() { for (const s of this.users.values()) for (const u of s) this.applyCol(u); }

  private rectsOf(t: TileDef): Ent[] { return [PLATE ? t.plate : undefined, t.walls, t.props, ...(t.roofs ?? []), t.emit, t.lite].filter(Boolean) as Ent[]; }
  private meets(t: TileDef, x0: number, y0: number, x1: number, y1: number) { return this.rectsOf(t).some(e => e.x < x1 && e.x + e.w > x0 && e.y < y1 && e.y + e.h > y0); }
  private bytesOf(t: TileDef) { return this.rectsOf(t).reduce((n, e) => n + e.tw * e.th * (e === t.plate && !GL1 ? 4 / 3 : 1) * (this.format === 'rgba8unorm' ? 4 : 1), 0); }

  private stream() {
    const m = this.m!, V = this.view, M = 128;
    // the view plus a margin, and the same view pushed ~1.5 s along the camera's motion (90 frames)
    const px = this.vel.x * 90, py = this.vel.y * 90;
    const want = new Set<string>();
    // (studio 009) in a set swap the old set is only the fallback until the new one is ready: it keeps the tiles it has
    // but never loads another, and its bytes do not block the new set's (they go once it is ready). Before, the old set
    // held its share of the cap and the new set's tiles were refused: 126 refusals at the first snow stop vs 6 by day
    const fallback = this.layers.find(l => l.a === 0 && l.set === this.prev)?.set ?? '';
    for (const [key, t] of Object.entries(m.tiles)) {
      if (this.meets(t, V.x - M, V.y - M, V.x + V.w + M, V.y + V.h + M) || this.meets(t, V.x + px - M, V.y + py - M, V.x + px + V.w + M, V.y + py + V.h + M))
        for (const L of this.layers) { const k = key + '|' + L.set; if (L.set !== fallback || this.sets.has(k)) want.add(k); }
    }
    const now = performance.now();
    for (const k of want) { const t = this.sets.get(k); if (t) t.used = now; }
    this.curReady = [...want].filter(k => k.endsWith('|' + this.cur)).every(k => this.sets.get(k)?.state === 'ok');
    // after a jump (a teleport, a respawn, the street after a door) the tiles on screen are still streaming: main.ts keeps
    // the loading cover up for them (up to 4 s), so the block does not open on today's art and then swap
    const onView = Object.entries(m.tiles).filter(([, t]) => this.meets(t, V.x, V.y, V.x + V.w, V.y + V.h)).map(([k]) => k + '|' + this.cur);
    this.busy = performance.now() - this.jumpT < 4000 && onView.some(k => this.sets.get(k)?.state !== 'ok' && this.sets.get(k)?.state !== 'failed');
    // unload: every set not in the layers at once (one set resident, two only in a cross-fade), then the LRU over the cap
    const inLayers = new Set(this.layers.map(l => l.set));
    let dropped = false;
    for (const [k, t] of this.sets) if (!inLayers.has(t.set) && t.state !== 'loading') { this.unload(k, t); dropped = true; }
    // and a tile nothing has wanted for IDLE_MS (you walked on): the LRU's cap is the ceiling, not the target
    for (const [k, t] of this.sets) if (!want.has(k) && t.state !== 'loading' && now - t.used > IDLE_MS) { this.unload(k, t); dropped = true; }
    let total = this.gpuBytes();
    if (fallback) for (const t of this.sets.values()) if (t.set === fallback) total -= t.bytes;
    for (const k of [...want].filter(k => !this.sets.has(k))) {
      const [key, set] = k.split('|'), need = this.bytesOf(m.tiles[key]);
      if (total + need > CAP_BYTES) {   // make room from the least recently wanted, never from what this frame wants
        const lru = [...this.sets.entries()].filter(([kk, t]) => !want.has(kk) && t.state !== 'loading' && t.set !== fallback).sort((a, b) => a[1].used - b[1].used);
        while (total + need > CAP_BYTES && lru.length) { const [kk, t] = lru.shift()!; total -= t.bytes; this.unload(kk, t); dropped = true; }
        if (total + need > CAP_BYTES) { this.capped++; continue; }
      }
      total += need;
      this.fetchTile(key, set, need);
    }
    if (dropped) this.reapply();
  }

  private unload(k: string, t: TS) {
    this.sets.delete(k);
    const s = this.users.get(t.key);
    if (s) for (const u of [...s]) if (u.spr.destroyed) this.retire(s, u);   // (before its sources go: see retire)
    if (s) for (const u of s) { if (u.base === t.set || u.ovSet === t.set) { this.applyCol(u); } for (const fk of [...u.frames.keys()]) if (fk === t.set || fk.startsWith(t.set + ':')) { u.frames.get(fk)!.destroy(false); u.frames.delete(fk); } }
    for (const p of this.pieces) if (p.key === t.key) { if (p.base === t.set) { p.base = ''; p.spr.visible = false; p.spr.texture = Texture.EMPTY; rebuild(p.spr); } if (p.ovSet === t.set) { p.ovSet = ''; p.ov.visible = false; p.ov.texture = Texture.EMPTY; rebuild(p.ov); } }
    for (const tx of t.tex.values()) tx.destroy(true);
  }

  private url(key: string, set: string, kind: string) {
    const name = kind.includes(':') ? `${kind.split(':')[0]}_${key}_${kind.split(':')[1]}` : `${kind}_${key}`;
    return new URL(`${this.base}${this.m!.sets[set].dir}${name}.ktx2`, location.href).href;
  }

  private async fetchTile(key: string, set: string, bytes: number) {
    const k = key + '|' + set, t: TS = { key, set, state: 'loading', tex: new Map(), bytes, used: performance.now() };
    this.sets.set(k, t);
    const d = this.m!.tiles[key];
    const kinds = [...(['plate', 'walls', 'props', 'emit', 'lite'] as const).filter(x => d[x] && (x !== 'plate' || PLATE)), ...(d.roofs ?? []).map(r => 'roof:' + r.b)];
    const LR = this.m!.lightpack?.res;
    const resOf = (k: string) => k.startsWith('roof:') ? this.m!.roof_res : k === 'emit' ? LR!.emit : k === 'lite' ? LR!.lite : this.m!.res;
    try {
      await Promise.all(kinds.map(async kind => {
        // loaded at resolution 1 (its size in texels), then told its texels a logical px (RES, ROOF_RES, the light pack's):
        // the resolution setter keeps the texels and shrinks the size
        const t1 = await Promise.race([
          loadKtx(this.url(key, set, kind)),
          new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), LOAD_TIMEOUT))]);
        t1.source.resolution = resOf(kind);
        const tex = new Texture({ source: t1.source });
        tex.source.scaleMode = 'linear'; tex.source.alphaMode = 'premultiplied-alpha';
        // WebGL 1 (the studio harness's lavapipe; old phones) has no mipmaps on a non-power-of-two texture: the plate keeps
        // its first level there (it is drawn at a texel a device px; the levels are for a zoomed-out desktop)
        const src = tex.source as any;
        if (GL1 && Array.isArray(src.resource) && src.resource.length > 1 && ((src.pixelWidth & (src.pixelWidth - 1)) || (src.pixelHeight & (src.pixelHeight - 1)))) { src.resource = [src.resource[0]]; src.mipLevelCount = 1; }
        t.tex.set(kind, tex);
      }));
      if (this.sets.get(k) !== t) { for (const kind of t.tex.keys()) t.tex.get(kind)?.destroy(true); return; }   // evicted while it loaded
      t.state = 'ok';
      const s = this.users.get(key); if (s) for (const u of s) this.applyCol(u);
    } catch (e) {
      t.state = 'failed'; this.failed++; if (this.failedKeys.length < 8) this.failedKeys.push(`${set}/${key}`);
      console.warn('paintblock: tile failed', set, key, e);
    }
  }

  /** each frame (after the cutaway): drop columns that were destroyed (their chunk went or was rebuilt) */
  sync() {
    if (++this.syncTick % 120 !== 0) return;
    for (const s of this.users.values()) for (const u of [...s]) if (u.spr.destroyed) this.retire(s, u);
  }
  /** a column whose sprite is gone (its chunk was dropped or rebuilt): its night and window overlays and frames go too, at
   *  once. They used to wait for sync() (every 120 frames) while still parented in the band, and when the LRU unloaded
   *  their tile in that window its sources were destroyed under them: the next render threw "reading 'addressModeU'"
   *  and the game loop stopped for good (studio 008c, the director's G3 freeze on the GPU worker; paint_evict_check.mjs) */
  private retire(s: Set<ColUser>, u: ColUser) {
    s.delete(u); this.dmgCols.delete(u.spr); u.ov?.destroy(); u.ov = null; u.em?.destroy(); u.em = null;
    for (const f of u.frames.values()) f.destroy(false); u.frames.clear();
  }

  /** the light probe at a ground point (world-screen logical px): 0..1, or -1 outside the plate (the night set's own) */
  probe(sx: number, sy: number): number {
    if (!this.probeLum || !this.probeM) return -1;
    const P = this.probeM, i = Math.floor((sx - P.x) / P.step), j = Math.floor((sy - P.y) / P.step);
    if (i < 0 || j < 0 || i >= this.probeW || j >= this.probeH) return -1;
    return this.probeLum[j * this.probeW + i];
  }
  /** the probe is read off its small PNG once (CPU pixels: it is the one painted asset the client reads) */
  async loadProbe() {
    const p = this.m?.probe?.night ?? (this.m ? Object.values(this.m.probe)[0] : null); if (!p) return;
    const im = new Image(); im.src = this.base + p.file; await new Promise<void>((res, rej) => { im.onload = () => res(); im.onerror = () => rej(new Error('probe')); });
    const cv = document.createElement('canvas'); cv.width = im.width; cv.height = im.height;
    const g = cv.getContext('2d', { willReadFrequently: true })!; g.drawImage(im, 0, 0);
    const d = g.getImageData(0, 0, im.width, im.height).data; this.probeW = im.width; this.probeH = im.height; this.probeM = p;
    this.probeLum = new Float32Array(im.width * im.height);
    for (let i = 0; i < this.probeLum.length; i++) this.probeLum[i] = d[i * 4 + 3] > 8 ? d[i * 4] / 255 : -1;
  }

  /** bytes of the painted textures resident on the GPU (block-compressed: 1 byte a texel; rgba if the device has none) */
  gpuBytes(): number { let b = 0; for (const t of this.sets.values()) b += t.bytes; return Math.round(b); }
  /** (the readout's check that lit windows draw: painted columns with a window overlay, and how many of them show) */
  emStats() { let n = 0, vis = 0, a = 0; for (const s of this.users.values()) for (const u of s) if (u.em) { n++; if (u.em.visible && u.em.parent) { vis++; a = Math.max(a, u.em.alpha); } } return { n, vis, a, miss: this.missCols, ex: this.missEx }; }
  /** outer wall columns of painted buildings in the painted rect that found no tile holding their rect (today's art) */
  missCols = 0; missEx: string[] = []; private missSeen = new Set<string>();
  resident(): number { let n = 0; for (const t of this.sets.values()) if (t.state === 'ok') n++; return n; }
  readout(): string {
    const L = this.layers.map(l => l.a < 1 ? `${l.set}${l.a.toFixed(2)}` : l.set).join('+');
    return `${this.format} ${(this.gpuBytes() / 1048576).toFixed(1)}/${(CAP_BYTES / 1048576) | 0}MB res ${this.resident()} fail ${this.failed}${this.capped ? ' cap ' + this.capped : ''}${this.emptyProps ? ' empty ' + this.emptyProps : ''}${this.ghosts.size ? ' back ' + this.backCount() : ''} dmg ${this.dmgCols.size} ${L}`;
  }
}

/** the ?perf=1 readout: median and p95 of the frame interval over the last 120 frames, top right; with the painted block (not ?art=a) its
 *  block's texture format, GPU MB / cap, chunks (tile sets) resident and failed, damaged painted columns, the set mix, and
 *  the game hour (more() gives the hour and the characters' line) */
export function perfReadout(more?: () => string) {
  const el = document.createElement('div');
  el.style.cssText = 'position:fixed;right:4px;top:4px;z-index:99;font:11px monospace;color:#fff;background:rgba(0,0,0,.6);padding:2px 4px;pointer-events:none;white-space:pre;text-align:right';
  document.body.appendChild(el);
  const iv: number[] = []; let last = 0, n = 0, shown = 0;
  const tick = (t: number) => {
    if (last) { iv.push(t - last); if (iv.length > 120) iv.shift(); }
    last = t;
    // (every 30 frames or half a second, whichever comes first: on a slow device 30 frames is many seconds stale)
    if ((++n % 30 === 0 || t - shown > 500) && iv.length > 10) {
      shown = t;
      const s = [...iv].sort((a, b) => a - b), q = (p: number) => s[Math.min(s.length - 1, Math.floor(p * s.length))];
      const pb = (window as any).__dm?.paint as PaintBlock | null | undefined;
      el.textContent = `${ART_B ? 'b ' + paintState : 'a'} med ${q(0.5).toFixed(1)} p95 ${q(0.95).toFixed(1)} ms` +
        (ART_B && pb?.ok ? '\n' + pb.readout() : '') + (more ? '\n' + more() : '') + ((window as any).__propsB ? '\n' + (window as any).__propsB() : '');   // (studio 009p: isoart.ts readoutB)
      (window as any).__perfRead = { med: q(0.5), p95: q(0.95), paint: ART_B && pb?.ok ? { fmt: pb.format, gpuMB: pb.gpuBytes() / 1048576, resident: pb.resident(), failed: pb.failed, failedKeys: pb.failedKeys, capped: pb.capped, dmg: pb.dmgCols.size, layers: pb.layers, lightsOn: pb.lightsOn, dark: (pb as any).lastDark, em: pb.emStats() } : null };
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}
